import React, { useEffect, useState } from 'react';
import { LuCircleAlert } from 'react-icons/lu';
import { graphqlRequest } from '../../utils/graphqlRequest';
import { useAdminPageSearch } from '../../utils/useAdminPageSearch';
import './styles/ProjectFinance.css';

const PROJECT_LIST_QUERY = `
  query GetFinanceProjectList {
    getFinanceProjectList {
      id
      name
      clientName
    }
  }
`;

const PROJECT_FINANCE_QUERY = `
  query GetProjectFinance($projectId: String!) {
    getProjectFinance(projectId: $projectId) {
      id
      projectName
      clientName
      expectedRevenue
      totalExpenses
      netMarginPercent
      netProfit
      outstanding
      estimatedCost
      variance
      paymentStatus
      totalRevenue
    }
  }
`;

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export default function ProjectFinance() {
  const { get, patch } = useAdminPageSearch();
  const urlProject = get('project');
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [financeData, setFinanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await graphqlRequest(PROJECT_LIST_QUERY);
      const projectList = result.getFinanceProjectList || [];
      setProjects(projectList);
      if (projectList.length > 0) {
        const match = projectList.some((p) => p.id === urlProject);
        setSelectedProjectId(match ? urlProject : projectList[0].id);
        if (!match) patch({ project: projectList[0].id });
      }
    } catch (err) {
      console.error('Failed to fetch project list:', err);
      setError('Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectFinance = async (projectId) => {
    if (!projectId) return;
    try {
      setFinanceLoading(true);
      const result = await graphqlRequest(PROJECT_FINANCE_QUERY, { projectId });
      setFinanceData(result.getProjectFinance);
    } catch (err) {
      console.error('Failed to fetch project finance:', err);
      setFinanceData(null);
    } finally {
      setFinanceLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProjectId) {
      fetchProjectFinance(selectedProjectId);
    }
  }, [selectedProjectId]);

  if (loading) {
    return (
      <div className="project-finance-page">
        <div className="pf-loading">
          <div className="spinner" />
          <p>Loading projects...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="project-finance-page">
        <div className="pf-error">
          <LuCircleAlert size={40} />
          <p>{error}</p>
          <button onClick={fetchProjects}>Retry</button>
        </div>
      </div>
    );
  }

  const d = financeData;
  const costProgress =
    d && d.estimatedCost > 0
      ? Math.min((d.totalExpenses / d.estimatedCost) * 100, 100)
      : 0;

  return (
    <div className="project-finance-page">
      {/* TITLE */}
      <div className="project-title">
        <h1>Project Finance</h1>
        <p>Monitor financial health of individual projects</p>
      </div>

      {/* PROJECT SELECT */}
      <div className="project-select-card">
        {projects.length === 0 ? (
          <p className="no-projects">No projects found</p>
        ) : (
          <select
            value={selectedProjectId}
            onChange={(e) => {
              const id = e.target.value;
              setSelectedProjectId(id);
              patch({ project: id });
            }}
            className="project-dropdown"
          >
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} - {p.clientName}
              </option>
            ))}
          </select>
        )}
      </div>

      {financeLoading ? (
        <div className="pf-loading">
          <div className="spinner" />
          <p>Loading project finances...</p>
        </div>
      ) : d ? (
        <>
          {/* PROJECT NAME */}
          <h3>{d.projectName}</h3>
          <p className="project-client">{d.clientName}</p>

          {/* KPI CARDS */}
          <div className="project-kpis">
            <div className="kpi-card kpi-blue">
              <h4>Expected Revenue</h4>
              <h2>{formatCurrency(d.expectedRevenue)}</h2>
            </div>

            <div className="kpi-card kpi-red">
              <h4>Project Expenses</h4>
              <h2>{formatCurrency(d.totalExpenses)}</h2>
            </div>

            <div className="kpi-card kpi-green">
              <h4>Net Margin</h4>
              <h2>{d.netMarginPercent.toFixed(1)}%</h2>
              <small>{formatCurrency(d.netProfit)} profit</small>
            </div>

            <div className="kpi-card">
              <h4>Outstanding</h4>
              <h2>{formatCurrency(d.outstanding)}</h2>
            </div>
          </div>

          {/* PROGRESS */}
          <div className="progress-box">
            <strong>Cost Progress</strong>
            <p className="progress-sub">
              {formatCurrency(d.totalExpenses)} spent of{' '}
              {formatCurrency(d.estimatedCost)} estimated
            </p>

            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${costProgress}%` }}
              />
            </div>

            <p className="variance-text">
              {d.variance >= 0 ? '+' : ''}
              {formatCurrency(Math.abs(d.variance))}{' '}
              {d.variance >= 0 ? 'under estimate' : 'over estimate'}
            </p>
          </div>

          {/* TABS */}
          <div className="finance-tabs">
            <span className="active">Summary</span>
            <span>Income</span>
            <span>Expenses</span>
          </div>

          {/* BREAKDOWN */}
          <div className="breakdown-card">
            <div className="breakdown-row">
              <span>Total Revenue</span>
              <span className="green">{formatCurrency(d.totalRevenue)}</span>
            </div>

            <div className="breakdown-row">
              <span>Total Expenses</span>
              <span className="red">{formatCurrency(d.totalExpenses)}</span>
            </div>

            <div className="breakdown-row">
              <span>Net Profit</span>
              <span className="profit">{formatCurrency(d.netProfit)}</span>
            </div>

            <div className="breakdown-row">
              <span>Estimated Cost</span>
              <span>{formatCurrency(d.estimatedCost)}</span>
            </div>

            <div className="breakdown-row">
              <span>Variance</span>
              <span className={d.variance >= 0 ? 'green' : 'red'}>
                {d.variance >= 0 ? '+' : ''}
                {formatCurrency(Math.abs(d.variance))}
              </span>
            </div>

            <div className="breakdown-row">
              <span>Payment Status</span>
              <span className="badge">{d.paymentStatus}</span>
            </div>
          </div>
        </>
      ) : (
        <div className="pf-empty">
          <p>Select a project to view financial details</p>
        </div>
      )}
    </div>
  );
}
